import { ReactNode, useMemo, useState } from 'react'
import { Check, CircleSlash, Eye, Settings2Icon, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Button, buttonVariants } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  RaidPlanAssignmentEntity,
  RaidPlanEncounterAssignmentAbility,
} from 'app/frontend/types/custom/RaidPlan'
import { useTeam } from 'app/frontend/base/config/context/team'
import BlizzardIcon from 'app/frontend/base/components/BlizzardIcon'
import CharacterRoleIcon from 'app/frontend/base/components/CharacterRoleIcon'
import ConfirmDialog from 'app/frontend/base/components/ConfirmDialog'
import { getClass } from 'app/frontend/base/services/characterValues'
import TeamPlanAssignmentScopeCard from './TeamPlanAssignmentScopeCard'
import { v4 as uuid } from 'uuid'
import {
  AssignmentViewMode,
  getEncounterAssignmentAbilities,
  getAssignmentScopes,
  normalizeAssignmentBox,
  normalizeAssignmentCast,
} from './assignmentTimeline'
import { getAssignmentEntityLabel } from './assignmentEntityLabels'
import getAssignmentEntityIdentity from './assignmentEntityIdentity'
import { getDuplicateIdentitiesByScopeId } from './assignmentDuplicateIdentities'
import SpellIcon from './SpellIcon'
import { useTeamPlanTimeline } from './TeamPlanTimelineContext'
import { formatTimelineTimestamp } from './timelineMath'
import { cn } from '@/lib/utils'

type Scope = {
  id: string
  label: string
  castIndexes: number[]
}

type PaletteEntity = {
  id: string
  entity: RaidPlanAssignmentEntity
  label: ReactNode
  category: 'collection' | 'group' | 'specific_character' | 'by_role'
}

const extractUuid = (value: string) => {
  const match = value.match(
    /[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}/i,
  )
  return match?.[0] ?? null
}

const raidPlanBaseUrl = 'https://raidstrats.gg/planner?view='

const TeamPlanAssignments = () => {
  const { t } = useTranslation()
  const { team } = useTeam()
  const { timeline, computed, canEdit, updateTimeline } = useTeamPlanTimeline()
  const [openRaidPlanSpellId, setOpenRaidPlanSpellId] = useState<number | null>(
    null,
  )
  const [confirmRemoveAbilitySpellId, setConfirmRemoveAbilitySpellId] =
    useState<number | null>(null)

  const encounterRowBySpellId = useMemo(
    () =>
      computed.encounterAssignmentRows.reduce<
        Record<number, typeof computed.encounterAssignmentRows[number]>
      >((acc, row) => {
        acc[row.spellId] = row
        return acc
      }, {}),
    [computed.encounterAssignmentRows],
  )

  const selectedSpellIds = useMemo(
    () =>
      getEncounterAssignmentAbilities(timeline)
        .map((ability) => ability.spell_id)
        .filter((spellId) => Boolean(encounterRowBySpellId[spellId])),
    [encounterRowBySpellId, timeline],
  )

  const selectedRows = useMemo(
    () =>
      selectedSpellIds
        .map((spellId) => encounterRowBySpellId[spellId])
        .filter(Boolean),
    [encounterRowBySpellId, selectedSpellIds],
  )

  const abilityOptions = useMemo(
    () =>
      computed.encounterAssignmentRows
        .filter((row) => !selectedSpellIds.includes(row.spellId))
        .map((row) => ({
          spellId: row.spellId,
          label: row.name,
          color: row.color,
        })),
    [computed.encounterAssignmentRows, selectedSpellIds],
  )

  const specializationById = useMemo(
    () =>
      computed.specializations.reduce<
        Record<
          number,
          {
            name: string
            icon: string
            classId: number
            className: string
          }
        >
      >((acc, specialization) => {
        acc[specialization.id] = {
          name: specialization.name,
          icon: specialization.icon,
          classId: specialization.classId,
          className: specialization.className,
        }
        return acc
      }, {}),
    [computed.specializations],
  )

  const memberInfoByCharacterId = useMemo(
    () =>
      (team.members ?? []).reduce<
        Record<number, { name: string; className: string }>
      >((acc, membership) => {
        const character = membership.characterReference
        acc[character.id] = {
          name: character.name,
          className: character.classId
            ? getClass(character.classId.toString()) ?? 'Unknown'
            : 'Unknown',
        }
        return acc
      }, {}),
    [team.members],
  )

  const paletteEntities = useMemo<PaletteEntity[]>(() => {
    const memberRoleOrder: Record<string, number> = {
      tank: 0,
      heal: 1,
      healer: 1,
      melee: 2,
      ranged: 3,
      dps: 4,
      damage: 4,
    }
    const roleLabel = (
      label: string,
      role: 'tank' | 'heal' | 'damage' | 'ranged' | 'melee',
    ) => (
      <span className='inline-flex items-center gap-1.5 min-w-0'>
        <CharacterRoleIcon role={role} size='small' className='shrink-0' />
        <span className='truncate'>{label}</span>
      </span>
    )
    const memberEntities = (team.members ?? [])
      .filter(
        (membership) =>
          membership.teamRank?.raid_visibility &&
          !membership.teamRank?.for_alts,
      )
      .sort((left, right) => {
        const leftRoleOrder =
          memberRoleOrder[left.role] ?? Number.MAX_SAFE_INTEGER
        const rightRoleOrder =
          memberRoleOrder[right.role] ?? Number.MAX_SAFE_INTEGER
        if (leftRoleOrder !== rightRoleOrder) {
          return leftRoleOrder - rightRoleOrder
        }
        return left.characterReference.name.localeCompare(
          right.characterReference.name,
        )
      })
      .map((membership) => {
        const character = membership.characterReference
        const specialization = membership.specId
          ? specializationById[membership.specId]
          : undefined
        const characterClassId = character.classId
        const roleIcon = membership.role === 'heal' ? 'heal' : membership.role
        const memberLabel = character.name
        return {
          id: `entity:member:${character.id}`,
          entity: {
            kind: 'member' as const,
            character_id: character.id,
            spec_id: membership.specId,
            role: membership.role,
            label: memberLabel,
          },
          label: (
            <span className='inline-flex items-center gap-1.5 min-w-0'>
              {specialization?.icon ? (
                <BlizzardIcon
                  iconName={specialization.icon}
                  size='small'
                  className='shrink-0'
                />
              ) : (
                <CharacterRoleIcon
                  role={roleIcon}
                  size='small'
                  className='shrink-0'
                />
              )}
              <span
                className='truncate'
                style={
                  characterClassId
                    ? { color: computed.classColor(characterClassId) }
                    : undefined
                }
              >
                {memberLabel}
              </span>
            </span>
          ),
          category: 'specific_character' as const,
        }
      })

    return [
      {
        id: 'entity:collection:everyone',
        entity: {
          kind: 'collection' as const,
          collection: 'everyone' as const,
        },
        label: t(
          'dashboard.team.plans.assignments.entities.collection.everyone',
        ),
        category: 'collection' as const,
      },
      {
        id: 'entity:collection:tanks',
        entity: { kind: 'collection' as const, collection: 'tanks' as const },
        label: roleLabel(
          t('dashboard.team.plans.assignments.entities.collection.tanks'),
          'tank',
        ),
        category: 'collection' as const,
      },
      {
        id: 'entity:collection:healers',
        entity: { kind: 'collection' as const, collection: 'healers' as const },
        label: roleLabel(
          t('dashboard.team.plans.assignments.entities.collection.healers'),
          'heal',
        ),
        category: 'collection' as const,
      },
      {
        id: 'entity:collection:dps',
        entity: { kind: 'collection' as const, collection: 'dps' as const },
        label: roleLabel(
          t('dashboard.team.plans.assignments.entities.collection.dps'),
          'damage',
        ),
        category: 'collection' as const,
      },
      {
        id: 'entity:collection:ranged',
        entity: { kind: 'collection' as const, collection: 'ranged' as const },
        label: roleLabel(
          t('dashboard.team.plans.assignments.entities.collection.ranged'),
          'ranged',
        ),
        category: 'collection' as const,
      },
      {
        id: 'entity:collection:melee',
        entity: { kind: 'collection' as const, collection: 'melee' as const },
        label: roleLabel(
          t('dashboard.team.plans.assignments.entities.collection.melee'),
          'melee',
        ),
        category: 'collection' as const,
      },
      ...Array.from({ length: 8 }, (_, index) => ({
        id: `entity:group:${index + 1}`,
        entity: {
          kind: 'group' as const,
          group: (index + 1) as 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8,
        },
        label: t('dashboard.team.plans.assignments.entities.group', {
          count: index + 1,
        }),
        category: 'group' as const,
      })),
      ...memberEntities,
      {
        id: 'entity:by-role:anyone',
        entity: { kind: 'by_role' as const, role: 'anyone' as const },
        label: t('dashboard.team.plans.assignments.entities.by-role.anyone'),
        category: 'by_role' as const,
      },
      ...(['tank', 'healer', 'dps', 'ranged', 'melee'] as const).map(
        (role) => ({
          id: `entity:by-role:${role}`,
          entity: { kind: 'by_role' as const, role },
          label: roleLabel(
            t(`dashboard.team.plans.assignments.entities.by-role.${role}`),
            role === 'healer' ? 'heal' : role === 'dps' ? 'damage' : role,
          ),
          category: 'by_role' as const,
        }),
      ),
    ]
  }, [computed.classColor, specializationById, t, team.members])
  const paletteLabelByIdentity = useMemo(
    () =>
      new Map(
        paletteEntities.map((entry) => [
          getAssignmentEntityIdentity(entry.entity),
          entry.label,
        ]),
      ),
    [paletteEntities],
  )

  const getNormalizedAbility = (
    spellId: number,
    castCount: number,
    assignmentGroups: number,
  ): RaidPlanEncounterAssignmentAbility => {
    const stored =
      timeline.encounter_assignments?.abilities?.[spellId.toString()]
    return {
      spell_id: spellId,
      casts: Array.from({ length: castCount }, (_, castIndex) =>
        normalizeAssignmentCast(stored?.casts?.[castIndex], assignmentGroups),
      ),
      message: stored?.message,
      offset_seconds: stored?.offset_seconds,
      raid_plan_link: stored?.raid_plan_link,
      view_mode: stored?.view_mode,
    }
  }

  const ensureAbilityOnTimeline = (
    abilitiesBySpellId: Record<string, RaidPlanEncounterAssignmentAbility>,
    abilityOrder: number[],
    spellId: number,
    castCount: number,
    assignmentGroups: number,
  ) => {
    const abilityKey = spellId.toString()
    const existingAbility = abilitiesBySpellId[abilityKey]
    if (existingAbility) {
      abilitiesBySpellId[abilityKey] = {
        ...existingAbility,
        casts: Array.from({ length: castCount }, (_, castIndex) =>
          normalizeAssignmentCast(
            existingAbility.casts?.[castIndex],
            assignmentGroups,
          ),
        ),
      }
      return abilitiesBySpellId[abilityKey]
    }
    const created = {
      spell_id: spellId,
      casts: Array.from({ length: castCount }, () =>
        normalizeAssignmentCast(undefined, assignmentGroups),
      ),
      message: undefined,
      offset_seconds: undefined,
      raid_plan_link: undefined,
      view_mode: undefined,
    }
    abilitiesBySpellId[abilityKey] = created
    abilityOrder.push(spellId)
    return created
  }

  const updateAbility = (
    spellId: number,
    castCount: number,
    assignmentGroups: number,
    updater: (ability: RaidPlanEncounterAssignmentAbility) => void,
    pushHistory = true,
  ) => {
    updateTimeline((next) => {
      if (!next.encounter_assignments) {
        next.encounter_assignments = { abilities: {}, ability_order: [] }
      }
      next.encounter_assignments.abilities =
        next.encounter_assignments.abilities ?? {}
      next.encounter_assignments.ability_order =
        next.encounter_assignments.ability_order ?? []
      const ability = ensureAbilityOnTimeline(
        next.encounter_assignments.abilities,
        next.encounter_assignments.ability_order,
        spellId,
        castCount,
        assignmentGroups,
      )
      updater(ability)
    }, pushHistory)
  }

  const addAbility = (spellId: number) => {
    const row = encounterRowBySpellId[spellId]
    if (!row) return
    updateAbility(spellId, row.entries.length, row.assignmentGroups, () => null)
  }

  const removeAbility = (spellId: number) => {
    updateTimeline((next) => {
      if (!next.encounter_assignments?.abilities) return
      delete next.encounter_assignments.abilities[spellId.toString()]
      next.encounter_assignments.ability_order =
        next.encounter_assignments.ability_order?.filter(
          (abilitySpellId) => abilitySpellId !== spellId,
        ) ?? []
    }, true)
  }

  return (
    <div className='space-y-3'>
      <div className='flex flex-wrap items-center gap-2'></div>

      {selectedRows.length === 0 ? (
        <div className='py-8 text-sm text-muted-foreground text-center'>
          {t('dashboard.team.plans.assignments.empty')}
        </div>
      ) : null}

      {selectedRows.map((row) => {
        const abilityAssignment = getNormalizedAbility(
          row.spellId,
          row.entries.length,
          row.assignmentGroups,
        )
        const abilityViewMode: AssignmentViewMode =
          abilityAssignment.view_mode ?? 'all_together'
        const scopes: Scope[] = getAssignmentScopes(
          row.entries,
          abilityViewMode,
          t,
        )
        const duplicateIdentitiesByScopeId = getDuplicateIdentitiesByScopeId({
          ability: abilityAssignment,
          scopes,
          assignmentGroups: row.assignmentGroups,
          viewMode: abilityViewMode,
        })
        const assignmentUsageCountByIdentity = scopes.reduce<
          Record<string, number>
        >((acc, scope) => {
          const scopeCountByIdentity = scope.castIndexes.reduce<
            Record<string, number>
          >((scopeAcc, castIndex) => {
            const cast = normalizeAssignmentCast(
              abilityAssignment.casts[castIndex],
              row.assignmentGroups,
            )
            const castCountByIdentity = cast.boxes.reduce<
              Record<string, number>
            >((castAcc, box) => {
              normalizeAssignmentBox(box).entities.forEach((entity) => {
                const identity = getAssignmentEntityIdentity(entity)
                castAcc[identity] = (castAcc[identity] ?? 0) + 1
              })
              return castAcc
            }, {})
            Object.entries(castCountByIdentity).forEach(([identity, count]) => {
              scopeAcc[identity] = Math.max(scopeAcc[identity] ?? 0, count)
            })
            return scopeAcc
          }, {})
          Object.entries(scopeCountByIdentity).forEach(([identity, count]) => {
            acc[identity] = (acc[identity] ?? 0) + count
          })
          return acc
        }, {})
        const raidPlanValue = (abilityAssignment.raid_plan_link ?? '').trim()
        const extractedRaidPlanId = extractUuid(raidPlanValue)
        const raidPlanId = extractedRaidPlanId ?? raidPlanValue
        const raidPlanUrl = `${raidPlanBaseUrl}${raidPlanId}`
        const hasRaidPlanId = Boolean(raidPlanId)
        const hasValidRaidPlanId = Boolean(extractedRaidPlanId)
        const hasAssignedEntities = abilityAssignment.casts.some((cast) =>
          normalizeAssignmentCast(cast, row.assignmentGroups).boxes.some(
            (box) => normalizeAssignmentBox(box).entities.length > 0,
          ),
        )
        return (
          <div key={row.spellId} className='flex flex-col gap-2 mb-8'>
            <div className='flex items-center justify-between gap-2'>
              <a
                href={`https://wowhead.com/spell=${row.spellId}`}
                target='_blank'
                rel='noreferrer'
                className='flex items-center gap-2 text-xl min-w-0'
                style={{ color: row.color }}
              >
                <SpellIcon
                  spellId={row.spellId}
                  className='w-8 h-8 rounded border border-border'
                />
                <span className='truncate'>{row.name}</span>
              </a>
              {canEdit ? (
                <div className='flex items-center gap-2'>
                  <Tabs
                    value={abilityViewMode}
                    onValueChange={(value) =>
                      updateAbility(
                        row.spellId,
                        row.entries.length,
                        row.assignmentGroups,
                        (ability) => {
                          ability.view_mode = value as AssignmentViewMode
                        },
                        true,
                      )
                    }
                  >
                    <TabsList>
                      <TabsTrigger value='all_together'>
                        {t('dashboard.team.plans.assignments.view-modes.all')}
                      </TabsTrigger>
                      <TabsTrigger value='alternating'>
                        {t(
                          'dashboard.team.plans.assignments.view-modes.alternating',
                        )}
                      </TabsTrigger>
                      <TabsTrigger value='all_separate'>
                        {t(
                          'dashboard.team.plans.assignments.view-modes.separate',
                        )}
                      </TabsTrigger>
                    </TabsList>
                  </Tabs>
                  <Popover
                    open={openRaidPlanSpellId === row.spellId}
                    onOpenChange={(open) =>
                      setOpenRaidPlanSpellId(open ? row.spellId : null)
                    }
                  >
                    <PopoverTrigger asChild>
                      <Button type='button' variant='outline'>
                        <Settings2Icon size={16} />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className='w-72 space-y-2'>
                      <p className='text-xs text-muted-foreground'>
                        {t('dashboard.team.plans.assignments.note-options')}
                      </p>
                      <Input
                        value={abilityAssignment.message ?? ''}
                        onChange={(event) =>
                          updateAbility(
                            row.spellId,
                            row.entries.length,
                            row.assignmentGroups,
                            (ability) => {
                              ability.message = event.target.value || undefined
                            },
                            false,
                          )
                        }
                        onBlur={() => updateTimeline(() => null, true)}
                        placeholder={`Soak ${row.name}`}
                        className='h-8 text-xs'
                        disabled={!canEdit}
                      />
                      <div className='flex items-center gap-2'>
                        <Input
                          type='number'
                          step='0.1'
                          value={abilityAssignment.offset_seconds ?? ''}
                          placeholder={'0'}
                          onChange={(event) => {
                            const value = event.target.value
                            const parsed = Number(value)
                            updateAbility(
                              row.spellId,
                              row.entries.length,
                              row.assignmentGroups,
                              (ability) => {
                                ability.offset_seconds =
                                  value === '' || !Number.isFinite(parsed)
                                    ? undefined
                                    : parsed
                              },
                              false,
                            )
                          }}
                          onBlur={() => updateTimeline(() => null, true)}
                          className='h-8 text-xs'
                          disabled={!canEdit}
                        />
                        <span className='text-xs text-muted-foreground whitespace-nowrap'>
                          {t('dashboard.team.plans.assignments.offset-seconds')}
                        </span>
                      </div>
                    </PopoverContent>
                  </Popover>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        type='button'
                        variant='outline'
                        className={cn(
                          'min-w-20 justify-between gap-2',
                          hasValidRaidPlanId ? 'border-primary' : '',
                        )}
                      >
                        <span>
                          {t('dashboard.team.plans.assignments.raid-plan-link')}
                        </span>
                        <span className='inline-flex h-4 w-4 items-center justify-center'>
                          {hasValidRaidPlanId ? (
                            <Check className='h-4 w-4 text-primary' />
                          ) : (
                            <CircleSlash className='h-4 w-4 text-muted-foreground' />
                          )}
                        </span>
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className='w-96 space-y-2'>
                      <div className='flex items-center gap-2'>
                        <Input
                          value={abilityAssignment.raid_plan_link ?? ''}
                          onChange={(event) =>
                            updateAbility(
                              row.spellId,
                              row.entries.length,
                              row.assignmentGroups,
                              (ability) => {
                                ability.raid_plan_link =
                                  event.target.value || undefined
                              },
                              false,
                            )
                          }
                          onPaste={(event) => {
                            const pasted = event.clipboardData.getData('text')
                            const extractedUuid = extractUuid(pasted)
                            if (!extractedUuid) return
                            event.preventDefault()
                            updateAbility(
                              row.spellId,
                              row.entries.length,
                              row.assignmentGroups,
                              (ability) => {
                                ability.raid_plan_link = extractedUuid
                              },
                              false,
                            )
                          }}
                          onBlur={() => updateTimeline(() => null, true)}
                          placeholder={t(
                            'dashboard.team.plans.assignments.raid-plan-link-placeholder',
                          )}
                          className='h-8 text-xs'
                          disabled={!canEdit}
                        />
                        {hasRaidPlanId ? (
                          <a
                            href={raidPlanUrl}
                            target='_blank'
                            rel='noreferrer'
                            className={cn(
                              buttonVariants({
                                variant: 'outline',
                                size: 'icon',
                              }),
                              'h-8 w-8 shrink-0',
                            )}
                          >
                            <Eye className='h-4 w-4' />
                          </a>
                        ) : (
                          <span
                            className={cn(
                              buttonVariants({
                                variant: 'outline',
                                size: 'icon',
                              }),
                              'h-8 w-8 shrink-0 pointer-events-none opacity-50',
                            )}
                          >
                            <Eye className='h-4 w-4' />
                          </span>
                        )}
                        <Button
                          type='button'
                          variant='outline'
                          size='icon'
                          className='h-8 w-8 shrink-0'
                          onClick={() => {
                            updateAbility(
                              row.spellId,
                              row.entries.length,
                              row.assignmentGroups,
                              (ability) => {
                                ability.raid_plan_link = undefined
                              },
                              true,
                            )
                            setOpenRaidPlanSpellId(null)
                          }}
                          disabled={
                            !canEdit ||
                            !(abilityAssignment.raid_plan_link ?? '').trim()
                          }
                        >
                          <Trash2 className='h-4 w-4' />
                        </Button>
                      </div>
                    </PopoverContent>
                  </Popover>
                  <Button
                    type='button'
                    variant='outline'
                    onClick={() => {
                      if (!hasAssignedEntities) {
                        removeAbility(row.spellId)
                        return
                      }
                      setConfirmRemoveAbilitySpellId(row.spellId)
                    }}
                  >
                    <Trash2 className='h-4 w-4' />
                  </Button>
                </div>
              ) : null}
            </div>

            {scopes.map((scope) => {
              const representativeCast = normalizeAssignmentCast(
                abilityAssignment.casts[scope.castIndexes[0]],
                row.assignmentGroups,
              )
              const scopeBoxes = Array.from(
                { length: row.assignmentGroups },
                (_, boxIndex) =>
                  normalizeAssignmentBox(representativeCast.boxes[boxIndex]),
              )
              const duplicateIdentities =
                duplicateIdentitiesByScopeId[scope.id] ?? new Set<string>()
              const scopeEntitiesByGroup = scopeBoxes.map((box) => box.entities)
              const raidPlanPlayers = scopeEntitiesByGroup
                .flat()
                .flatMap((entity) => {
                  if (entity.kind !== 'member') {
                    return []
                  }
                  const memberInfo =
                    memberInfoByCharacterId[entity.character_id]
                  const specialization = entity.spec_id
                    ? specializationById[entity.spec_id]
                    : undefined
                  const className =
                    specialization?.className ?? memberInfo?.className
                  const name = memberInfo?.name ?? entity.label
                  if (!className || !name) {
                    return []
                  }
                  return [{ name, className }]
                })
              return (
                <TeamPlanAssignmentScopeCard
                  key={scope.id}
                  scope={scope}
                  castTimestamps={scope.castIndexes.map((castIndex) =>
                    formatTimelineTimestamp(row.entries[castIndex]?.time ?? 0),
                  )}
                  entitiesByGroup={scopeEntitiesByGroup}
                  assignmentGroups={row.assignmentGroups}
                  canEdit={canEdit}
                  paletteEntities={paletteEntities}
                  assignmentUsageCountByIdentity={
                    assignmentUsageCountByIdentity
                  }
                  onSave={(nextEntitiesByGroup) =>
                    updateAbility(
                      row.spellId,
                      row.entries.length,
                      row.assignmentGroups,
                      (ability) => {
                        scope.castIndexes.forEach((castIndex) => {
                          const cast = normalizeAssignmentCast(
                            ability.casts[castIndex],
                            row.assignmentGroups,
                          )
                          nextEntitiesByGroup.forEach(
                            (nextEntities, boxIndex) => {
                              cast.boxes[boxIndex] = {
                                ...normalizeAssignmentBox(cast.boxes[boxIndex]),
                                entities: nextEntities.map((entity) => ({
                                  ...entity,
                                  id: entity.id ?? uuid(),
                                })),
                              }
                            },
                          )
                          ability.casts[castIndex] = cast
                        })
                      },
                      true,
                    )
                  }
                  assignLabel={t('dashboard.team.plans.assignments.assign')}
                  raidPlanId={hasValidRaidPlanId ? extractedRaidPlanId : null}
                  raidPlanPlayers={raidPlanPlayers}
                  abilityViewMode={abilityViewMode}
                  duplicateIdentities={duplicateIdentities}
                  getEntityLabel={(entity) =>
                    paletteLabelByIdentity.get(
                      getAssignmentEntityIdentity(entity),
                    ) ?? getAssignmentEntityLabel(t, entity)
                  }
                  groupLabel={(count) =>
                    t('dashboard.team.plans.assignments.group-label', { count })
                  }
                />
              )
            })}
          </div>
        )
      })}

      {canEdit ? (
        <div className='flex justify-end'>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type='button'
                variant='outline'
                disabled={!abilityOptions.length}
              >
                {t('dashboard.team.plans.assignments.add-ability')}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align='end'>
              {abilityOptions.map((option) => (
                <DropdownMenuItem
                  key={option.spellId}
                  onClick={() => addAbility(option.spellId)}
                >
                  <div className='flex items-center gap-2 min-w-0'>
                    <SpellIcon
                      spellId={option.spellId}
                      className='w-4 h-4 rounded border border-border shrink-0'
                    />
                    <span className='truncate' style={{ color: option.color }}>
                      {option.label}
                    </span>
                  </div>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ) : null}
      <ConfirmDialog
        open={Boolean(confirmRemoveAbilitySpellId)}
        descriptionKey='dashboard.team.plans.assignments.remove-ability-confirm'
        onCancel={() => setConfirmRemoveAbilitySpellId(null)}
        onContinue={() => {
          if (!confirmRemoveAbilitySpellId) return
          removeAbility(confirmRemoveAbilitySpellId)
          setConfirmRemoveAbilitySpellId(null)
        }}
        variant='destructive'
      />
    </div>
  )
}

export default TeamPlanAssignments
